/**
 * The scoring-model proposal as a form (C2-36).
 *
 * "Approve as Noted" used to seed a raw JSON textarea for every proposal
 * type. A `methodology` proposal (the AI's reading of the SOW: the domains
 * and their weights) is the one type a reviewer edits by hand, so it gets
 * domain rows with a name and a weight. The rows write back the same JSON
 * the API applies (`domains[]` of `{ name, weight }`, other keys kept), so
 * the form and the Advanced JSON editor are two views of one value.
 *
 * Pure: no React here, so the rules are unit-tested on their own.
 */

export interface ScoringDomainRow {
  /** Stable key for React, kept across edits. */
  key: string;
  name: string;
  /** As typed; empty when the domain carries no weight. */
  weight: string;
  /** The original domain object, so keys the form does not show survive. */
  source: Record<string, unknown> | null;
}

/** Proposal types the form applies to; every other type edits JSON only. */
export function hasScoringModelForm(proposalType: string): boolean {
  return proposalType === 'methodology';
}

function asRecord(value: unknown): Record<string, unknown> {
  return value !== null && typeof value === 'object' && !Array.isArray(value)
    ? (value as Record<string, unknown>)
    : {};
}

function nameOf(domain: unknown): string {
  if (typeof domain === 'string') return domain.trim();
  const r = asRecord(domain);
  for (const key of ['name', 'domain', 'label']) {
    const v = r[key];
    if (typeof v === 'string' && v.trim().length > 0) return v.trim();
  }
  return '';
}

function weightOf(domain: unknown): string {
  const w = asRecord(domain).weight;
  if (typeof w === 'number' && Number.isFinite(w)) return String(w);
  if (typeof w === 'string' && w.trim().length > 0) return w.trim();
  return '';
}

let rowSeq = 0;
function nextKey(): string {
  rowSeq += 1;
  return `domain-${rowSeq}`;
}

/** One row per entry of `value.domains` (strings or objects); none when there is no list. */
export function scoringModelRows(value: unknown): ScoringDomainRow[] {
  const domains = asRecord(value).domains;
  if (!Array.isArray(domains)) return [];
  return domains.map((d) => ({
    key: nextKey(),
    name: nameOf(d),
    weight: weightOf(d),
    source: typeof d === 'string' ? null : asRecord(d),
  }));
}

export function emptyScoringDomainRow(): ScoringDomainRow {
  return { key: nextKey(), name: '', weight: '', source: null };
}

/**
 * The value with the rows written back into `domains`. A row with a weight
 * writes `{ name, weight: <number> }`; one without writes `{ name }` only;
 * the row's other original keys are kept. The rest of the value is untouched.
 */
export function applyScoringModelRows(
  value: unknown,
  rows: readonly ScoringDomainRow[],
): Record<string, unknown> {
  const base = asRecord(value);
  const domains = rows.map((row) => {
    const { weight: _dropped, name: _name, ...rest } = row.source ?? {};
    const out: Record<string, unknown> = { ...rest, name: row.name.trim() };
    const weight = parseWeight(row.weight);
    if (weight !== null) out.weight = weight;
    return out;
  });
  return { ...base, domains };
}

/** A weight as a number, or null when blank or not a number. */
export function parseWeight(text: string): number | null {
  const trimmed = text.trim().replace(/%$/, '');
  if (trimmed.length === 0) return null;
  const n = Number(trimmed);
  return Number.isFinite(n) ? n : null;
}

/** The sum of the rows' weights; null when no row has one. */
export function weightTotal(rows: readonly ScoringDomainRow[]): number | null {
  let total: number | null = null;
  for (const row of rows) {
    const w = parseWeight(row.weight);
    if (w === null) continue;
    total = (total ?? 0) + w;
  }
  return total;
}

/** "Weights total 1.00" / "Weights total 100", as the reviewer typed them. */
export function weightTotalText(rows: readonly ScoringDomainRow[]): string | null {
  const total = weightTotal(rows);
  if (total === null) return null;
  const shown = Number.isInteger(total) ? String(total) : total.toFixed(2);
  const complete = Math.abs(total - 1) < 0.005 || Math.abs(total - 100) < 0.5;
  return complete
    ? `Weights total ${shown}`
    : `Weights total ${shown}; a complete model totals 1.00 (or 100 as percentages)`;
}

/** The first problem with the rows, or null when they can be submitted. */
export function scoringModelProblem(rows: readonly ScoringDomainRow[]): string | null {
  if (rows.length === 0) return 'Add at least one domain.';
  for (const [i, row] of rows.entries()) {
    if (row.name.trim().length === 0) return `Domain ${i + 1} needs a name.`;
    if (row.weight.trim().length > 0) {
      const w = parseWeight(row.weight);
      if (w === null) return `The weight of "${row.name.trim()}" must be a number.`;
      if (w < 0) return `The weight of "${row.name.trim()}" cannot be negative.`;
    }
  }
  const names = rows.map((r) => r.name.trim().toLowerCase());
  const dup = names.find((n, i) => names.indexOf(n) !== i);
  if (dup) return `"${rows[names.indexOf(dup)]!.name.trim()}" is listed twice.`;
  return null;
}

/** The JSON text as the object the API accepts, or why it cannot be. */
export function parseEditedValue(
  text: string,
): { value: Record<string, unknown> } | { error: string } {
  let parsed: unknown;
  try {
    parsed = JSON.parse(text);
  } catch (err) {
    const detail = err instanceof SyntaxError ? err.message.replace(/^JSON\.parse: /, '') : '';
    return { error: `The edited value is not valid JSON${detail ? ` (${detail})` : ''}.` };
  }
  if (parsed === null || typeof parsed !== 'object' || Array.isArray(parsed)) {
    return { error: 'The edited value must be a JSON object.' };
  }
  return { value: parsed as Record<string, unknown> };
}

/**
 * What "Approve as Noted" submits: the JSON parsed, and for a scoring model
 * its domain rows checked. One problem at a time, as the editor shows it.
 */
export function validateEditedValue(
  proposalType: string,
  text: string,
): { value: Record<string, unknown> } | { error: string } {
  const parsed = parseEditedValue(text);
  if ('error' in parsed) return parsed;
  if (hasScoringModelForm(proposalType)) {
    const problem = scoringModelProblem(scoringModelRows(parsed.value));
    if (problem) return { error: problem };
  }
  return parsed;
}

/** The value as the Advanced editor shows it. */
export function formatEditedValue(value: unknown): string {
  return JSON.stringify(value ?? {}, null, 2);
}
